import { useEffect, useState, useRef } from "react";
import BackofficeLayout, { Card, Btn, fmtEUR } from "./BackofficeLayout";
import { api, API } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Upload, Plus, Trash2, Save, Filter, Wand2, Download } from "lucide-react";

const CATS = [
  "loyer_recu", "loyer_verse",
  "credit_mensualite", "credit_deblocage",
  "charges_energie", "charges_eau", "charges_assurance",
  "charges_generales", "charges_impots", "charges_taxes",
  "solaire_revente", "solaire_charge",
  "travaux_gros_oeuvre", "travaux_vrd", "travaux_etudes",
  "travaux_finitions", "travaux_autres",
  "apport", "distribution", "compte_courant_associe",
  "virement_interne", "frais_bancaires",
  "revenus_autres", "non_categorise",
];

const emptyManual = { account_id: "", date: new Date().toISOString().slice(0, 10),
                       label: "", amount: 0, category: "non_categorise",
                       project_id: "", notes: "" };

export default function BankTransactionsAdmin() {
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [txs, setTxs] = useState([]);
  const [filters, setFilters] = useState({ account_id: "", category: "", date_from: "", date_to: "" });
  const [csvAccount, setCsvAccount] = useState("");
  const [manual, setManual] = useState(emptyManual);
  const [showManual, setShowManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    const [a, p, t] = await Promise.all([
      api.get("/backoffice/bank/accounts").then((r) => r.data),
      api.get("/backoffice/projects").then((r) => r.data),
      api.get(`/backoffice/bank/transactions?${params.toString()}`).then((r) => r.data),
    ]);
    setAccounts(a); setProjects(p); setTxs(t);
    if (!csvAccount && a.length) setCsvAccount(a[0].id);
  };

  useEffect(() => { load(); }, [filters]);

  const importCSV = async (file) => {
    if (!csvAccount) { toast.error("Sélectionne un compte de destination"); return; }
    if (!file) return;
    const fd = new FormData();
    fd.append("account_id", csvAccount);
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/backoffice/bank/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import : ${r.data.inserted} ajouts, ${r.data.skipped_duplicates} doublons ignorés`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'import");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyRules = async () => {
    setBusy(true);
    try {
      const r = await api.post("/backoffice/rules/apply");
      toast.success(`${r.data.recategorized} transactions recatégorisées`);
      load();
    } catch {
      toast.error("Erreur");
    } finally { setBusy(false); }
  };

  const updateTx = async (id, field, value) => {
    const body = { [field]: value };
    try {
      await api.put(`/backoffice/bank/transactions/${id}`, body);
      setTxs((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
    } catch { toast.error("Erreur de mise à jour"); }
  };

  const removeTx = async (id) => {
    if (!window.confirm("Supprimer cette transaction ?")) return;
    await api.delete(`/backoffice/bank/transactions/${id}`);
    load();
  };

  const submitManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/backoffice/bank/transactions", {
        ...manual, amount: parseFloat(manual.amount) || 0,
        project_id: manual.project_id || null,
      });
      toast.success("Transaction ajoutée");
      setManual(emptyManual); setShowManual(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const bulkCat = async (cat) => {
    if (!selected.size) return;
    await api.post("/backoffice/bank/transactions/bulk-categorize", {
      ids: Array.from(selected), category: cat,
    });
    toast.success(`${selected.size} transactions mises à jour`);
    setSelected(new Set());
    load();
  };

  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const totalIn = txs.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalOut = txs.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);

  return (
    <BackofficeLayout
      title="Transactions bancaires"
      subtitle="Import CSV manuel (fallback). Synchronisation automatique via Powens/Bridge à venir."
      actions={
        <>
          <Btn variant="ghost" onClick={applyRules} disabled={busy} data-testid="bof-apply-rules">
            <Wand2 className="w-3.5 h-3.5" /> Appliquer règles
          </Btn>
          <a href={`${API}/backoffice/export/transactions.csv`} target="_blank" rel="noreferrer">
            <Btn variant="ghost" data-testid="bof-export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Btn>
          </a>
          <Btn onClick={() => { setManual({ ...emptyManual, account_id: accounts[0]?.id || "" }); setShowManual(true); }} data-testid="bof-tx-manual-btn">
            <Plus className="w-3.5 h-3.5" /> Manuel
          </Btn>
        </>
      }
    >
      <Toaster theme="dark" position="top-right" />

      {/* Import CSV */}
      <Card className="mb-6" data-testid="bof-csv-import-card">
        <div className="flex items-center gap-2 mb-3">
          <Upload className="w-4 h-4 text-amber-400" />
          <div className="font-bold text-white text-sm">Import CSV bancaire</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Compte de destination</span>
            <select value={csvAccount} onChange={(e) => setCsvAccount(e.target.value)} className="input" data-testid="bof-csv-account-select">
              <option value="">— Choisir —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.entity_name}</option>)}
            </select>
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Fichier CSV</span>
            <input ref={fileRef} type="file" accept=".csv,text/csv"
                   onChange={(e) => importCSV(e.target.files?.[0])}
                   className="input" data-testid="bof-csv-file-input" />
          </div>
          <div className="text-xs text-slate-500 leading-tight max-w-xs">
            Colonnes attendues : <code>date</code>, <code>libellé</code>, <code>montant</code> (ou <code>débit/crédit</code>).
            Séparateur <code>;</code> ou <code>,</code>.
          </div>
        </div>
      </Card>

      {/* Manual form */}
      {showManual && (
        <Card className="mb-6" data-testid="bof-manual-form">
          <form onSubmit={submitManual} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select required value={manual.account_id} onChange={(e) => setManual({ ...manual, account_id: e.target.value })} className="input">
              <option value="">Compte</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input type="date" required value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} className="input" />
            <input required placeholder="Libellé" value={manual.label} onChange={(e) => setManual({ ...manual, label: e.target.value })} className="input" />
            <input type="number" step="0.01" required placeholder="Montant (- pour débit)" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} className="input" />
            <select value={manual.category} onChange={(e) => setManual({ ...manual, category: e.target.value })} className="input">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={manual.project_id} onChange={(e) => setManual({ ...manual, project_id: e.target.value })} className="input">
              <option value="">— Projet (optionnel) —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="md:col-span-3 flex gap-2">
              <Btn type="submit" data-testid="bof-manual-save"><Save className="w-3.5 h-3.5" /> Ajouter</Btn>
              <Btn type="button" variant="ghost" onClick={() => setShowManual(false)}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6" data-testid="bof-tx-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-amber-400" />
          <div className="font-bold text-white text-sm">Filtres</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select value={filters.account_id} onChange={(e) => setFilters({ ...filters, account_id: e.target.value })} className="input" data-testid="bof-filter-account">
            <option value="">Tous comptes</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="input" data-testid="bof-filter-category">
            <option value="">Toutes catégories</option>
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="input" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="input" />
        </div>
      </Card>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <Card className="mb-4 bg-amber-500/10 border-amber-500/40" data-testid="bof-bulk-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-amber-200 text-sm font-bold">{selected.size} sélectionnées</span>
            <span className="text-xs text-slate-400">Catégoriser en :</span>
            <select onChange={(e) => e.target.value && bulkCat(e.target.value)} className="input max-w-xs" defaultValue="">
              <option value="">— Choisir catégorie —</option>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Btn variant="ghost" onClick={() => setSelected(new Set())}>Désélectionner tout</Btn>
          </div>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Entrées</div><div className="text-lg font-bold text-emerald-300">{fmtEUR(totalIn)}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sorties</div><div className="text-lg font-bold text-red-300">{fmtEUR(totalOut)}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Solde net</div><div className={`text-lg font-bold ${totalIn+totalOut >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtEUR(totalIn + totalOut)}</div></Card>
      </div>

      {/* Transactions table */}
      <Card data-testid="bof-tx-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="py-2 pr-2 w-8">
                  <input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(txs.map((t) => t.id)) : new Set())}
                         checked={selected.size === txs.length && txs.length > 0} />
                </th>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Libellé</th>
                <th className="py-2 pr-4 text-right">Montant</th>
                <th className="py-2 pr-4">Catégorie</th>
                <th className="py-2 pr-4">Projet</th>
                <th className="py-2 pr-4">Source</th>
                <th />
              </tr>
            </thead>
            <tbody data-testid="bof-tx-list">
              {txs.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-slate-500">Aucune transaction. Importez un CSV ou ajoutez manuellement.</td></tr>
              )}
              {txs.map((t) => (
                <tr key={t.id} className="border-b border-slate-800/50 hover:bg-slate-900/40" data-testid={`bof-tx-row-${t.id}`}>
                  <td className="py-2 pr-2">
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} />
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-slate-400">{t.date}</td>
                  <td className="py-2 pr-4">{t.label}</td>
                  <td className={`py-2 pr-4 text-right font-mono font-bold ${t.amount >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtEUR(t.amount)}</td>
                  <td className="py-2 pr-4">
                    <select value={t.category || "non_categorise"} onChange={(e) => updateTx(t.id, "category", e.target.value)}
                            className="bg-slate-800 border border-slate-700 text-xs rounded px-2 py-1"
                            data-testid={`bof-tx-cat-${t.id}`}>
                      {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-4">
                    <select value={t.project_id || ""} onChange={(e) => updateTx(t.id, "project_id", e.target.value || null)}
                            className="bg-slate-800 border border-slate-700 text-xs rounded px-2 py-1">
                      <option value="">—</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-4 text-xs text-slate-500">{t.source}</td>
                  <td className="py-2 pr-2">
                    <button onClick={() => removeTx(t.id)} className="text-red-400 hover:text-red-300" data-testid={`bof-tx-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <style>{`
        .input { background: rgba(15, 23, 42, 0.6); border: 1px solid rgb(51, 65, 85); color: white;
                 padding: 0.5rem 0.75rem; border-radius: 0.375rem; width: 100%; font-size: 0.875rem; }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}
